/**
 * AI Key Hook
 *
 * Manages OpenAI API key and Base URL in localStorage for BYOK (Bring Your Own Key).
 * Supports graceful degradation when key is not configured.
 * 
 * Supports custom API Base URL for:
 * - OpenAI proxy services (for users behind firewalls)
 * - OpenAI-compatible APIs (DeepSeek, Moonshot, etc.)
 * - Local models (Ollama with OpenAI-compatible endpoint)
 */

import { useState, useEffect, useCallback, createContext, useContext } from "react";
import type { ReactNode } from "react";

// ============================================================================
// Types
// ============================================================================

interface AIKeyState {
    aiKey: string | null;
    apiBaseUrl: string;
    chatModel: string;
    embeddingModel: string;
    isConfigured: boolean;
    isLoading: boolean;
}

interface AIKeyContextValue extends AIKeyState {
    /** Alias for aiKey - for backward compatibility */
    apiKey: string | null;
    setAIKey: (key: string) => void;
    setApiKey: (key: string) => void; // Alias for setAIKey
    setApiBaseUrl: (url: string) => void;
    setChatModel: (model: string) => void;
    setEmbeddingModel: (model: string) => void;
    clearAIKey: () => void;
    validateKey: (key: string) => boolean;
}

// ============================================================================
// Constants
// ============================================================================

const AI_KEY_STORAGE_KEY = "cloudledger_ai_key";
const AI_BASE_URL_STORAGE_KEY = "cloudledger_ai_base_url";
const AI_CHAT_MODEL_STORAGE_KEY = "cloudledger_ai_chat_model";
const AI_EMBEDDING_MODEL_STORAGE_KEY = "cloudledger_ai_embedding_model";
const DEFAULT_API_BASE_URL = "https://api.openai.com/v1";
const DEFAULT_CHAT_MODEL = "gpt-4o-mini";
const DEFAULT_EMBEDDING_MODEL = "text-embedding-3-small";

// ============================================================================
// Context
// ============================================================================

const AIKeyContext = createContext<AIKeyContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface AIKeyProviderProps {
    children: ReactNode;
}

export const AIKeyProvider = ({ children }: AIKeyProviderProps) => {
    const [state, setState] = useState<AIKeyState>({
        aiKey: null,
        apiBaseUrl: DEFAULT_API_BASE_URL,
        chatModel: DEFAULT_CHAT_MODEL,
        embeddingModel: DEFAULT_EMBEDDING_MODEL,
        isConfigured: false,
        isLoading: true,
    });

    // Load key and base URL from localStorage on mount
    useEffect(() => {
        const storedKey = localStorage.getItem(AI_KEY_STORAGE_KEY);
        const storedBaseUrl = localStorage.getItem(AI_BASE_URL_STORAGE_KEY);
        const storedChatModel = localStorage.getItem(AI_CHAT_MODEL_STORAGE_KEY);
        const storedEmbeddingModel = localStorage.getItem(AI_EMBEDDING_MODEL_STORAGE_KEY);
        setState({
            aiKey: storedKey,
            apiBaseUrl: storedBaseUrl || DEFAULT_API_BASE_URL,
            chatModel: storedChatModel || DEFAULT_CHAT_MODEL,
            embeddingModel: storedEmbeddingModel || DEFAULT_EMBEDDING_MODEL,
            isConfigured: !!storedKey,
            isLoading: false,
        });
    }, []);

    const setAIKey = useCallback((key: string) => {
        // Relaxed validation - some providers don't use sk- prefix
        if (key.length < 10) {
            throw new Error("Invalid API key format. Key is too short.");
        }

        localStorage.setItem(AI_KEY_STORAGE_KEY, key);
        setState((prev) => ({
            ...prev,
            aiKey: key,
            isConfigured: true,
            isLoading: false,
        }));
    }, []);

    const setApiBaseUrl = useCallback((url: string) => {
        // Validate URL format
        try {
            new URL(url);
        } catch {
            throw new Error("Invalid URL format");
        }

        // Remove trailing slash for consistency
        const normalizedUrl = url.replace(/\/$/, "");
        localStorage.setItem(AI_BASE_URL_STORAGE_KEY, normalizedUrl);
        setState((prev) => ({
            ...prev,
            apiBaseUrl: normalizedUrl,
        }));
    }, []);

    const setChatModel = useCallback((model: string) => {
        const trimmed = model.trim() || DEFAULT_CHAT_MODEL;
        localStorage.setItem(AI_CHAT_MODEL_STORAGE_KEY, trimmed);
        setState((prev) => ({ ...prev, chatModel: trimmed }));
    }, []);

    const setEmbeddingModel = useCallback((model: string) => {
        const trimmed = model.trim() || DEFAULT_EMBEDDING_MODEL;
        localStorage.setItem(AI_EMBEDDING_MODEL_STORAGE_KEY, trimmed);
        setState((prev) => ({ ...prev, embeddingModel: trimmed }));
    }, []);

    const clearAIKey = useCallback(() => {
        localStorage.removeItem(AI_KEY_STORAGE_KEY);
        localStorage.removeItem(AI_BASE_URL_STORAGE_KEY);
        localStorage.removeItem(AI_CHAT_MODEL_STORAGE_KEY);
        localStorage.removeItem(AI_EMBEDDING_MODEL_STORAGE_KEY);
        setState({
            aiKey: null,
            apiBaseUrl: DEFAULT_API_BASE_URL,
            chatModel: DEFAULT_CHAT_MODEL,
            embeddingModel: DEFAULT_EMBEDDING_MODEL,
            isConfigured: false,
            isLoading: false,
        });
    }, []);

    const validateKey = useCallback((key: string): boolean => {
        // Relaxed validation for non-OpenAI providers
        return key.length >= 10;
    }, []);

    return (
        <AIKeyContext.Provider value={{
            ...state,
            apiKey: state.aiKey, // Alias for backward compatibility
            setAIKey,
            setApiKey: setAIKey, // Alias for backward compatibility
            setApiBaseUrl,
            setChatModel,
            setEmbeddingModel,
            clearAIKey,
            validateKey
        }}>
            {children}
        </AIKeyContext.Provider>
    );
};

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access AI key state and methods
 *
 * @example
 * ```tsx
 * const { aiKey, apiBaseUrl, isConfigured, setAIKey, setApiBaseUrl, clearAIKey } = useAIKey();
 *
 * if (!isConfigured) {
 *   return <ConfigureAIButton />;
 * }
 * ```
 */
export const useAIKey = (): AIKeyContextValue => {
    const context = useContext(AIKeyContext);

    if (!context) {
        throw new Error("useAIKey must be used within AIKeyProvider");
    }

    return context;
};
